import { useEffect, useState } from 'react';
import { SHOT_WIDTH, siteShotUrl } from '../lib/web';

const RETRY_MS = 2500;
const MAX_ATTEMPTS = 6;

export type SiteShot =
  | { status: 'idle' | 'loading' | 'failed'; src: null }
  | { status: 'ready'; src: string };

const ready = new Map<string, string>();

/**
 * Resolves to a real capture of the page, never the capture service's
 * "generating" placeholder, which is smaller than the requested width.
 */
export function useSiteShot(url: string | null, enabled = true): SiteShot {
  const [shot, setShot] = useState<SiteShot>(() => {
    const cached = url ? ready.get(url) : undefined;
    return cached ? { status: 'ready', src: cached } : { status: 'idle', src: null };
  });

  useEffect(() => {
    if (!url || !enabled) return;
    const cached = ready.get(url);
    if (cached) {
      setShot({ status: 'ready', src: cached });
      return;
    }
    let alive = true;
    let timer = 0;
    setShot({ status: 'loading', src: null });

    const load = (attempt: number) => {
      const src = siteShotUrl(url, attempt);
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => {
        if (!alive) return;
        if (img.naturalWidth >= SHOT_WIDTH * 0.9) {
          ready.set(url, src);
          setShot({ status: 'ready', src });
        } else if (attempt + 1 < MAX_ATTEMPTS) {
          timer = window.setTimeout(() => load(attempt + 1), RETRY_MS);
        } else {
          setShot({ status: 'failed', src: null });
        }
      };
      img.onerror = () => alive && setShot({ status: 'failed', src: null });
      img.src = src;
    };
    load(0);

    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [url, enabled]);

  return shot;
}
